import { PhonemeTypeFilter } from "@/components/slides/toolbar/panels/phonics/PhonemeTypeFilter";
import { PhonemesGrid } from "@/components/slides/toolbar/panels/phonics/PhonemesGrid";
import { VoiceVariantCard } from "@/components/slides/toolbar/panels/phonics/VoiceVariantCard";
import {
  GetPhonemesFromMediaHouseDocument,
  GetPhonicsAudiosByPhonemeDocument,
  type Phoneme,
  type PhonemeType,
} from "@/generated/graphql";
import { useCopyFromMediaHouse } from "@/lib/hooks/medias/useCopyFromMediaHouse";
import { Button, LoadingSpinner, Modal } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import { IoClose, IoSearch } from "react-icons/io5";

/** A clip and the sound it records, which the slide stores together for pronunciation scoring. */
export interface PickedPhonics {
  media: { id: string; src: string; filename: string };
  phoneme: string;
  phonemeId: string;
}

function VoiceList({
  phoneme,
  onPick,
}: {
  phoneme: Phoneme;
  onPick: (picked: PickedPhonics) => void;
}) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const { copyFromMediaHouse, copying } = useCopyFromMediaHouse();

  const { data, loading } = useQuery(GetPhonicsAudiosByPhonemeDocument, {
    variables: { phonemeId: phoneme.id },
    fetchPolicy: "cache-and-network",
  });

  const audios = data?.phonicsAudiosByPhonemeFromMediaHouse ?? [];

  const pick = async (audioId: string) => {
    const media = await copyFromMediaHouse(audioId, phoneme.id);
    onPick({ media, phoneme: phoneme.phoneme, phonemeId: phoneme.id });
  };

  if (loading && audios.length === 0) {
    return (
      <div className="flex justify-center py-10">
        <LoadingSpinner />
      </div>
    );
  }

  if (audios.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
        This sound has no recording yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {audios.map((audio) => (
        <VoiceVariantCard
          key={audio.id}
          audio={audio}
          isPlaying={playingId === audio.id}
          onTogglePlay={() => setPlayingId((prev) => (prev === audio.id ? null : audio.id))}
          onAdd={() => void pick(audio.id)}
          copying={copying}
        />
      ))}
    </div>
  );
}

/**
 * The phonics library as the slide editor offers it, for an author filling in a drafted slide. The
 * editor's own panel writes through its store, so the browsing is shared but the picking is not.
 */
export function PhonicsLibraryPicker({
  isOpen,
  onClose,
  onPick,
}: {
  isOpen: boolean;
  onClose: () => void;
  onPick: (picked: PickedPhonics) => void;
}) {
  const [type, setType] = useState<PhonemeType | null>(null);
  const [search, setSearch] = useState("");
  const [opened, setOpened] = useState<Phoneme | null>(null);

  const { data, loading, error } = useQuery(GetPhonemesFromMediaHouseDocument, {
    variables: { phonemeType: null },
    fetchPolicy: "cache-first",
    skip: !isOpen,
  });

  const phonemes = data?.phonemesFromMediaHouse ?? [];
  const typeCounts = phonemes.reduce(
    (counts, one) => {
      counts[one.phonemeType] = (counts[one.phonemeType] ?? 0) + 1;
      return counts;
    },
    {} as Record<PhonemeType, number>
  );

  const term = search.trim().toLowerCase();
  const shown = phonemes.filter((one) => {
    const matchesType = !type || one.phonemeType === type;
    const matchesTerm =
      term === "" ||
      one.phoneme.toLowerCase().includes(term) ||
      (one.tags ?? []).some((tag) => tag.toLowerCase().includes(term));
    return matchesType && matchesTerm;
  });

  const close = () => {
    setOpened(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={opened ? `Pick a voice for "${opened.phoneme}"` : "Pick a sound"}
      size="xl"
    >
      <div className="space-y-3">
        {opened ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<HiArrowLeft className="h-4 w-4" />}
              onClick={() => setOpened(null)}
            >
              All sounds
            </Button>
            <VoiceList
              phoneme={opened}
              onPick={(picked) => {
                onPick(picked);
                close();
              }}
            />
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-800">
              <IoSearch size={14} className="shrink-0 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search phoneme…"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
              />
              {search !== "" && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <IoClose size={14} />
                </button>
              )}
            </div>

            <PhonemeTypeFilter activeType={type} onTypeChange={setType} typeCounts={typeCounts} />

            <div className="max-h-[55vh] overflow-y-auto">
              <PhonemesGrid
                phonemes={shown}
                loading={loading}
                error={error}
                disabled={false}
                onPhonemeClick={setOpened}
                showGrouped={!type && term === ""}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
